import styled from 'styled-components';
import {
  mqLargeMin,
  mqLargeMax,
  mqMediumMin,
} from 'styled/tools/media-queries';
import { TypeSubhead } from 'components/library/Typography';
import { variants } from '.';

export const HighlightBackground = styled.div`
  background: var(--fill-brand);
`;

export const HighlightSection = styled.section<{
  borderRadius?: string;
}>`
  background: var(--fill-primary);
  padding-inline: var(--spacing-16);
  padding-block: var(--spacing-16) var(--spacing-64);

  @media ${mqMediumMin} {
    padding-inline: var(--spacing-24);
    padding-block: var(--spacing-24) var(--spacing-72);
  }

  @media ${mqLargeMin} {
    padding-inline: var(--spacing-32);
    padding-block: var(--spacing-80);
  }
  border-radius: ${({ borderRadius }) => borderRadius ?? '0'};
`;

export const HighlightFrameTitle = styled(TypeSubhead)`
  left: var(--spacing-16);
  max-width: 18ch;
  position: absolute;
  top: var(--spacing-16);
  z-index: 2;

  @media ${mqMediumMin} {
    left: var(--spacing-40);
    top: var(--spacing-40);
  }

  &[data-pill]:before {
    content: attr(data-pill);
    color: var(--fill-brand);
    font-family: var(--ff);
    font-size: var(--font-size-350);
    font-weight: 500;
    height: 30px;
    text-transform: uppercase;
    border-radius: var(--radius-full);
    border: 1.5px solid currentColor;
    padding-inline: 12px;
    display: inline-flex;
    vertical-align: middle;
    align-items: center;
    margin-right: var(--spacing-8);
    margin-bottom: var(--spacing-8);
  }
`;

export const HighlightFrame = styled.div<{ blockSize?: string }>`
  border-radius: var(--radius-md);
  aspect-ratio: auto;
  align-items: center;
  display: flex;
  position: relative;
  overflow: hidden;

  & > picture,
  & img,
  & > video {
    block-size: ${({ blockSize }) => blockSize ?? '120%'};
    inline-size: 120%;
    object-fit: cover;
    object-position: top center;
  }
`;

export const HighlightHeadline = styled.div<{ variant: variants }>`
  align-self: center;
  grid-column: 1 / -1;
  margin-bottom: 38px;
  & > * + * {
    margin-top: var(--spacing-24);
  }

  @media ${mqLargeMin} {
    margin-bottom: var(--spacing-56);
  }

  ${({ variant }) =>
    variant === 'left' &&
    `
        @media ${mqLargeMin} {
            grid-column: 8 / span 5;
          }
  `}

  ${({ variant }) =>
    variant === 'right' &&
    `
        @media ${mqLargeMin} {
            grid-column: auto / span 6;
            max-width: 608px;            
          }
  `}

  ${({ variant }) =>
    (variant === 'below' || variant === 'below-2' || variant === 'below-3') &&
    `
      max-width: 656px;
    `}
`;

export const HighlightWrapper = styled.div<{ variant: variants }>`
  grid-column: 1 / -1;

  ${({ variant }) =>
    variant === 'left' &&
    `
          @media ${mqLargeMin} {
              grid-column: auto / span 6;
              grid-row: 1;
            }
    `}

  ${({ variant }) =>
    variant === 'right' &&
    `
          @media ${mqLargeMin} {
              grid-column: 7 / span 6;
              grid-row: 1;
            }
    `}
`;

export const HighlightGrid = styled.div<{ variant: variants; total: number }>`
  --columns: 2;
  --span: ${({ total }) => (total == 4 ? '2' : '3')};
  display: grid;
  grid-template-columns: repeat(var(--columns), minmax(10px, 1fr));
  gap: var(--spacing-16);

  ${({ total, variant }) =>
    total == 4 &&
    (variant === 'left' || variant === 'right') &&
    `
        @media ${mqLargeMax} {
          --span: 3;
          & .card:last-child {
            display: none;
          }
        }

  `}

  ${({ variant }) =>
    (variant === 'left' || variant === 'right') &&
    `
        --columns: 6;

        & .card {
            aspect-ratio: 1 / 1;
            @media ${mqLargeMin} {
              aspect-ratio: 16 / 14;
            }
            grid-column: auto / span var(--span);
        }

        & .card:nth-child(1) {
            aspect-ratio: 16 / 9;
            grid-column: 1 / -1;
        }
  `}

  ${({ variant }) =>
    variant === 'below' &&
    `
        --columns: 1;

          & .card {
              aspect-ratio: 1 / 1;
              grid-column: 1 / -1;
              grid-row: 1 / -1;
              justify-content: end;

              @media ${mqLargeMin} {
                aspect-ratio: 16 / 7;
              }
          }
    `}

  ${({ variant }) =>
    variant === 'below-2' &&
    `
        & .card:nth-child(1),
        & .card:nth-child(2) {
            grid-column: 1 / -1;

            @media ${mqLargeMin} {
              aspect-ratio: 16 / 11;
              grid-column: auto / span 1;
            }
        }
  `}

  ${({ variant }) =>
    variant === 'below-3' &&
    `
        --columns: 6;
        aspect-ratio: 1 / 1;

        @media ${mqLargeMin} {
          aspect-ratio: 16 / 6;
        }

        & .card:nth-child(1) {
            grid-column: 1 / -1;

            @media ${mqLargeMin} {
              grid-row: 1 / span 2;
              grid-column: 1 / span 5;
            }
        }

        & .card:nth-child(2),
        & .card:nth-child(3) {
            grid-column: auto / span 3;

            @media ${mqLargeMin} {
              grid-column: 6 / span 1;
            }
        }

        & .card:nth-child(2) {
          @media ${mqLargeMin} {
            grid-row: 1 / span 1;
          }
        }

        & .card:nth-child(3) {
          @media ${mqLargeMin} {
            grid-row: auto / span 1;
          }
        }
  `}
`;
